import api from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StoryStoreRef {
  _id: string;
  name?: string;
  slug?: string;
  logo?: string;
}

export interface AdminStory {
  _id: string;
  store?: StoryStoreRef;
  media?: {
    type?: 'image' | 'video';
    url?: string;
  };
  link?: {
    url?: string;
    text?: string;
  };
  createdAt: string;
  viewersCount: number;
  reportsCount: number;
  pendingReportsCount: number;
  hidden: boolean;
  hiddenReason?: string;
  hiddenAt?: string;
}

export type StoryStatusFilter = '' | 'active' | 'hidden' | 'reported';

export type StoryReportReason =
  | 'inappropriate'
  | 'violence'
  | 'fraud'
  | 'spam'
  | 'copyright'
  | 'other';

export type StoryReportStatus = 'pending' | 'reviewed' | 'dismissed' | 'actioned';

export interface StoryReporterRef {
  _id: string;
  name?: string;
  phone?: string;
  email?: string;
}

export interface AdminStoryReport {
  _id: string;
  storyId?: {
    _id: string;
    media?: { type?: string; url?: string };
    createdAt?: string;
    moderation?: { hidden?: boolean; reason?: string };
  } | null;
  storeId?: StoryStoreRef | null;
  reporterId?: StoryReporterRef | null;
  reason: StoryReportReason;
  description?: string;
  status: StoryReportStatus;
  snapshot?: {
    mediaUrl?: string;
    storeName?: string;
  };
  reviewedBy?: { _id: string; name?: string } | null;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface AdminStoriesResponse {
  stories: AdminStory[];
  pagination: Pagination;
}

export interface AdminStoryReportsResponse {
  reports: AdminStoryReport[];
  pagination: Pagination;
}

// ---------------------------------------------------------------------------
// Labels (Persian)
// ---------------------------------------------------------------------------

export const REPORT_REASON_LABELS: Record<StoryReportReason, string> = {
  inappropriate: 'محتوای نامناسب',
  violence: 'خشونت',
  fraud: 'کلاهبرداری',
  spam: 'اسپم',
  copyright: 'نقض حق نشر',
  other: 'سایر'
};

export const REPORT_STATUS_LABELS: Record<StoryReportStatus, string> = {
  pending: 'در انتظار بررسی',
  reviewed: 'بررسی شد',
  dismissed: 'رد شد',
  actioned: 'اقدام شد'
};

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class StoryAdminService {
  private storiesURL = '/admin/stories';
  private reportsURL = '/admin/story-reports';

  /**
   * لیست استوری‌ها برای مدیریت (فعال / مخفی / گزارش‌شده + جستجوی فروشگاه)
   */
  async getStories(
    page: number = 1,
    limit: number = 20,
    filters?: { status?: StoryStatusFilter; search?: string }
  ): Promise<AdminStoriesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search?.trim()) params.append('search', filters.search.trim());

    const response: any = await api.get(`${this.storiesURL}?${params.toString()}`);
    const data = response?.stories ? response : response?.data || {};
    return {
      stories: data.stories || [],
      pagination: {
        total: data.pagination?.total || 0,
        page: data.pagination?.page || page,
        limit: data.pagination?.limit || limit,
        pages: data.pagination?.pages || 0
      }
    };
  }

  /**
   * لیست گزارش‌های استوری (snapshot حتی بعد از انقضای استوری موجود است)
   */
  async getReports(
    page: number = 1,
    limit: number = 20,
    filters?: { status?: StoryReportStatus | ''; storyId?: string }
  ): Promise<AdminStoryReportsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    if (filters?.status) params.append('status', filters.status);
    if (filters?.storyId) params.append('storyId', filters.storyId);

    const response: any = await api.get(`${this.reportsURL}?${params.toString()}`);
    const data = response?.reports ? response : response?.data || {};
    return {
      reports: data.reports || [],
      pagination: {
        total: data.pagination?.total || 0,
        page: data.pagination?.page || page,
        limit: data.pagination?.limit || limit,
        pages: data.pagination?.pages || 0
      }
    };
  }

  /**
   * خروج از انتشار / بازگرداندن به انتشار
   */
  async setModeration(storyId: string, hidden: boolean, reason?: string): Promise<void> {
    await api.patch(`${this.storiesURL}/${storyId}/moderation`, {
      hidden,
      reason: reason?.trim() || undefined
    });
  }

  /**
   * تغییر وضعیت گزارش: reviewed | dismissed | actioned
   */
  async updateReportStatus(reportId: string, status: StoryReportStatus): Promise<void> {
    await api.patch(`${this.reportsURL}/${reportId}`, { status });
  }

  /**
   * حذف کامل استوری (مدیا هم حذف می‌شود)
   */
  async deleteStory(storyId: string): Promise<void> {
    await api.delete(`${this.storiesURL}/${storyId}`);
  }
}

export const storyAdminService = new StoryAdminService();
export default storyAdminService;
